import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MicIcon from "@mui/icons-material/Mic";
import {
  Avatar,
  Box,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogTitle,
  Icon,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
  Tabs,
  Tab,
  Chip,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { getPatientById, getMedicalRecords, getPatientHistory } from "../../../api/services/api";
import textCapitalization from "../../../components/textCapitalization";
import VoiceExerciseSummary from "./VoiceExerciseSummary";

function PatientProfile() {
  const theme = useTheme();
  const patientId = useParams();
  const [patientProfileId, setpatientProfileId] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();

  const { data } = useQuery(
    ["patient", patientId?.id],
    getPatientById
  );

  const { data: getMedicalRecordsData } = useQuery(
    [
      "medical-records",
      {
        patientProfileId: patientProfileId,
      },
    ],
    getMedicalRecords,
    {
      enabled: Boolean(patientProfileId),
    }
  );

  const { data: patientHistoryData } = useQuery(
    ["patient-history", patientProfileId],
    getPatientHistory,
    {
      enabled: Boolean(patientProfileId),
    }
  );

  const handleReports = (id: any) => {
    setpatientProfileId(id);
    setOpenDialog(true);
  };

  return (
    <Box>
      <Box mt={3}>
        <Box display="flex" gap={1} mb={2} alignItems="center">
          <ArrowBackIcon
            sx={{ cursor: "pointer" }}
            onClick={() => navigate("/patients")}
          />
          <Typography variant="h3">Client Details</Typography>
        </Box>

        <Card sx={{ mb: 2 }}>
          <Box display="flex" p="25px" alignItems="center">
            <Avatar
              variant="rounded"
              sx={{ height: "142px", width: "142px", fontSize: "100px" }}
              src={data?.data?.imageUrl}
              alt={data?.data?.fullName}
            />
            <Box pl={3} flex={1}>
              <NameBox title="Name" detail={data?.data?.fullName} />
            </Box>
            <Box flex={1} display="flex">
              <Box
                borderLeft="1px solid"
                mr={3}
                sx={{ opacity: "0.1" }}
                height="45px"
              />
              <NameBox
                title="Gender"
                detail={textCapitalization(data?.data?.gender || "")}
              />
            </Box>
            <Box flex={1} display="flex">
              <Box
                borderLeft="1px solid"
                mr={3}
                sx={{ opacity: "0.1" }}
                height="45px"
              />
              <NameBox title="Age" detail={data?.data?.age || "NA"} />
            </Box>
            <Box flex={1.3} display="flex">
              <Box
                borderLeft="1px solid"
                mr={3}
                sx={{ opacity: "0.1" }}
                height="45px"
              />
              <Box display="flex" gap={1} flexDirection="column">
                <Button
                  variant="outlined"
                  onClick={() => handleReports(data?.data?.id)}
                >
                  Medical Reports
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<MicIcon />}
                  onClick={() => navigate(`/patients/patient-profile/${patientId?.id}/voice-exercises`)}
                >
                  Voice Exercises
                </Button>
              </Box>
            </Box>
          </Box>
        </Card>

        {/* Voice Exercise Summary */}
        {data?.data?.id && (
          <VoiceExerciseSummary patientProfileId={data?.data?.id} />
        )}
      </Box>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: "20px" }}>Patient Medical History</DialogTitle>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ px: 3 }}>
          <Tab label="Session Reports" />
          <Tab label="Uploaded Records" />
        </Tabs>
        <DialogContent sx={{ minHeight: "400px" }} dividers>
          <Box width="100%" height="100%">
            {activeTab === 0 && (
              // Session Reports Tab
              <>
                {!patientHistoryData?.data?.sessions || patientHistoryData?.data?.sessions?.length === 0 ? (
                  <Typography variant="h6" sx={{ opacity: "0.7", textAlign: "center", py: 4 }}>
                    No Session Reports Found
                  </Typography>
                ) : (
                  <Box>
                    {patientHistoryData?.data?.sessions.map((session: any) => (
                      <Card key={session.id} sx={{ mb: 2, p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="bold">
                              Session: {session.sessionId}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Date: {new Date(session.date).toLocaleDateString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Time: {session.startAt} - {session.endAt}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Clinician: {session.clinician?.name}
                            </Typography>
                            {session.clinician?.designation && (
                              <Typography variant="caption" color="text.secondary">
                                {session.clinician.designation}
                              </Typography>
                            )}
                          </Box>
                          <Chip
                            label={session.consultationMode}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>

                        {session.reports && session.reports.length > 0 && (
                          <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>
                              Reports:
                            </Typography>
                            {session.reports.map((report: any) => (
                              <Box key={report.id} display="flex" alignItems="center" gap={1} mb={1}>
                                <AssignmentIcon fontSize="small" color="primary" />
                                <Typography variant="body2" flex={1}>
                                  {report.type} Report ({report.userType})
                                </Typography>
                                <a
                                  href={report.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ textDecoration: "none" }}
                                >
                                  <Button variant="outlined" size="small">
                                    <DownloadIcon fontSize="small" sx={{ mr: 0.5 }} />
                                    View
                                  </Button>
                                </a>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Card>
                    ))}
                  </Box>
                )}
              </>
            )}

            {activeTab === 1 && (
              // Uploaded Medical Records Tab
              <>
                {!getMedicalRecordsData?.data || getMedicalRecordsData?.data?.length === 0 ? (
                  <Typography variant="h6" sx={{ opacity: "0.7", textAlign: "center", py: 4 }}>
                    No Uploaded Medical Records Found
                  </Typography>
                ) : (
                  <Box>
                    {getMedicalRecordsData?.data?.map((record: any, index: number) => (
                      <Box key={index} mb={2}>
                        <List sx={{ py: 0 }}>
                          <ListItem
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: 1,
                            }}
                            secondaryAction={
                              <a
                                href={record?.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: "none" }}
                              >
                                <Button variant="outlined" size="small">
                                  <DownloadIcon sx={{ mr: 0.5 }} fontSize="small" />
                                  View
                                </Button>
                              </a>
                            }
                          >
                            <Box
                              sx={{
                                backgroundColor: theme.colors.primary.lighter,
                                p: 1.5,
                                borderRadius: "30px",
                                display: "flex",
                                justifyContent: "center",
                                mr: 2,
                              }}
                            >
                              <Icon sx={{ color: theme.colors.primary.main }}>
                                <DescriptionOutlinedIcon />
                              </Icon>
                            </Box>
                            <ListItemText
                              primary={`Medical Record ${index + 1}`}
                              secondary={new Date(record.createdAt).toLocaleDateString()}
                            />
                          </ListItem>
                        </List>
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default PatientProfile;

const NameBox = ({ title, detail }: any) => {
  return (
    <Box>
      <Typography variant="caption">{title}</Typography>
      <Typography variant="h5" sx={{ fontFamily: "Inter_regular" }}>
        {detail}
      </Typography>
    </Box>
  );
};
